import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Space, Popconfirm, message, Card, Typography, Input, Tag, Tooltip, Select, Modal } from 'antd';
import {
  EditOutlined, DeleteOutlined, DownloadOutlined,
  SearchOutlined, FileTextOutlined, ReloadOutlined, PlusOutlined, UploadOutlined,
} from '@ant-design/icons';
import { protocolAPI } from '../services/api';
import { brand, gray, modalWidth, radius } from '../theme/tokens';
import { EmptyState } from '../components/common';
import HwpxUploadPage from './HwpxUploadPage';
import ProtocolFormPage from './ProtocolFormPage';

/* 시험작물(품종) 필터 값 — 작물과 품종 3칸을 한 축으로 본다 */
const cropValues = (d: any): string[] => [
  d.test_crop, d.test_variety_efficacy, d.test_variety_damage1, d.test_variety_damage2,
].map((v: any) => (v || '').trim()).filter(Boolean);

/* 시험년도 정렬: 최근 연도 먼저, 숫자로 못 읽는 값은 뒤로 */
const yearRank = (v: string) => {
  const m = /\d{4}/.exec(v || '');
  return m ? Number(m[0]) : -1;
};

const ProtocolListPage: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [filtered, setFiltered] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [diseaseFilter, setDiseaseFilter] = useState('');
  const [cropFilter, setCropFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const navigate = useNavigate();

  const fetchData = () => {
    setLoading(true);
    protocolAPI.list()
      .then(({ data }) => { setData(data); setFiltered(data); })
      .catch(() => { message.error('시험계획서 목록을 불러오는데 실패했습니다.'); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    let result = data;
    if (yearFilter) result = result.filter((d: any) => (d.test_year || '') === yearFilter);
    if (diseaseFilter) result = result.filter((d: any) => d.target_disease === diseaseFilter);
    // 작물 드롭다운은 품종(약효/약해1/약해2)도 값으로 포함 — 둘 중 하나만 맞아도 통과
    if (cropFilter) result = result.filter((d: any) => cropValues(d).includes(cropFilter));
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((p: any) =>
        (p.protocol_code || '').toLowerCase().includes(q) ||
        (p.title || '').toLowerCase().includes(q) ||
        (p.institution || '').toLowerCase().includes(q) ||
        (p.manager || '').toLowerCase().includes(q) ||
        (p.test_crop || '').toLowerCase().includes(q)
      );
    }
    setFiltered(result);
  }, [search, data, diseaseFilter, cropFilter, yearFilter]);

  const handleDelete = async (id: number) => {
    try {
      await protocolAPI.delete(id);
      message.success('삭제되었습니다.');
      fetchData();
    } catch (err: any) {
      message.error(err.response?.data?.detail || '삭제에 실패했습니다.');
    }
  };

  const handleDownload = async (record: any) => {
    try {
      const { data } = await protocolAPI.downloadHwpx(record.id);
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${record.protocol_code || 'protocol'}_${record.id}.hwpx`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('HWPX 파일이 다운로드되었습니다.');
    } catch {
      message.error('다운로드에 실패했습니다.');
    }
  };

  const handleUploadClose = () => {
    setUploadModalOpen(false);
    fetchData();
  };

  const handleUploadEdit = (id: number) => {
    setUploadModalOpen(false);
    setEditId(id);
  };

  const handleEditClose = () => {
    setEditId(null);
    fetchData();
  };

  const columns = [
    {
      title: 'ID', dataIndex: 'id', key: 'id', width: 52,
      render: (v: number) => <Typography.Text type="secondary" style={{ fontSize: 12 }}>#{v}</Typography.Text>,
    },
    {
      title: '프로토콜 코드', dataIndex: 'protocol_code', key: 'protocol_code', width: 140,
      render: (v: string) => v ? <Tag color="blue" style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 12 }}>{v}</Tag> : <Typography.Text type="secondary">-</Typography.Text>,
    },
    {
      title: '항목명', dataIndex: 'title', key: 'title', ellipsis: true, minWidth: 200,
      render: (v: string) => <Typography.Text strong style={{ fontSize: 13 }}>{v}</Typography.Text>,
    },
    { title: '시험기관', dataIndex: 'institution', key: 'institution', width: 110, ellipsis: true },
    { title: '시험작물', dataIndex: 'test_crop', key: 'test_crop', width: 110, ellipsis: true },
    { title: '시험년도', dataIndex: 'test_year', key: 'test_year', width: 95, ellipsis: true },
    { title: '담당자', dataIndex: 'manager', key: 'manager', width: 140, ellipsis: true },
    {
      title: '작업', key: 'actions', width: 110, fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Space size={4}>
          <Tooltip title="편집">
            <Button type="text" size="small" icon={<EditOutlined />}
              style={{ color: brand.primary }}
              onClick={() => setEditId(record.id)} />
          </Tooltip>
          <Tooltip title="HWPX 다운로드">
            <Button type="text" size="small" icon={<DownloadOutlined />}
              style={{ color: '#0ea5e9' }}
              onClick={() => handleDownload(record)} />
          </Tooltip>
          <Popconfirm title="삭제하시겠습니까?" okText="삭제" cancelText="취소"
            onConfirm={() => handleDelete(record.id)}>
            <Tooltip title="삭제">
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FileTextOutlined style={{ color: brand.primary, fontSize: 18 }} />
            <div>
              <Typography.Title level={5} style={{ margin: 0 }}>시험계획서 목록</Typography.Title>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                총 {filtered.length}건{(search || yearFilter || diseaseFilter || cropFilter) && ` (검색결과)`}
              </Typography.Text>
            </div>
          </div>
        }
        extra={
          <Space>
            <Input
              placeholder="코드, 항목명, 기관, 담당자 검색"
              prefix={<SearchOutlined style={{ color: gray[400] }} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              style={{ width: 260, borderRadius: radius.md }}
            />
            <Select
              placeholder="시험년도"
              allowClear
              showSearch
              style={{ width: 118 }}
              value={yearFilter || undefined}
              onChange={(v) => setYearFilter(v || '')}
              options={Array.from(new Set(data.map((d: any) => (d.test_year || '').trim()).filter(Boolean)))
                .sort((a: any, b: any) => yearRank(b) - yearRank(a) || String(a).localeCompare(String(b)))
                .map((v: any) => ({ label: v, value: v }))}
            />
            <Select
              placeholder="대상병해충"
              allowClear
              showSearch
              style={{ width: 148 }}
              value={diseaseFilter || undefined}
              onChange={(v) => setDiseaseFilter(v || '')}
              options={Array.from(new Set(data.map((d: any) => (d.target_disease || '').trim()).filter(Boolean)))
                .sort((a: any, b: any) => String(a).localeCompare(String(b), 'ko'))
                .map((v: any) => ({ label: v, value: v }))}
            />
            <Select
              placeholder="시험작물(품종)"
              allowClear
              showSearch
              style={{ width: 158 }}
              value={cropFilter || undefined}
              onChange={(v) => setCropFilter(v || '')}
              options={Array.from(new Set(data.flatMap((d: any) => cropValues(d))))
                .sort((a: any, b: any) => String(a).localeCompare(String(b), 'ko'))
                .map((v: any) => ({ label: v, value: v }))}
            />
            <Tooltip title="새로고침">
              <Button icon={<ReloadOutlined />} onClick={fetchData} style={{ borderRadius: radius.md }} />
            </Tooltip>
            <Button
              icon={<UploadOutlined />}
              onClick={() => setUploadModalOpen(true)}
              style={{ borderRadius: radius.md }}
            >
              HWP 업로드
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setRegisterModalOpen(true)}
              style={{ borderRadius: radius.md, backgroundColor: brand.primary, borderColor: brand.primary }}
            >
              등록
            </Button>
          </Space>
        }
      >
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}건`,
          }}
          locale={{ emptyText: <EmptyState title="등록된 시험계획서가 없습니다" size="sm" /> }}
          scroll={{ x: 1000 }}
          size="small"
          onRow={(record) => ({
            style: { cursor: 'pointer' },
            onDoubleClick: () => setEditId(record.id),
          })}
        />
      </Card>

      <Modal
        title={
          <Space>
            <UploadOutlined style={{ color: brand.primary }} />
            <span>HWP / HWPX 파일 업로드</span>
          </Space>
        }
        open={uploadModalOpen}
        onCancel={() => setUploadModalOpen(false)}
        footer={null}
        width={900}
        destroyOnClose
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto', paddingTop: 8 } }}
      >
        <HwpxUploadPage
          onClose={handleUploadClose}
          onEditNavigate={handleUploadEdit}
        />
      </Modal>

      <Modal
        title={
          <Space>
            <PlusOutlined style={{ color: brand.primary }} />
            <span>새 시험계획서 등록</span>
          </Space>
        }
        open={registerModalOpen}
        onCancel={() => setRegisterModalOpen(false)}
        footer={null}
        width={1100}
        destroyOnClose
        styles={{ body: { height: 'calc(82vh - 55px)', padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
      >
        <ProtocolFormPage
          onClose={() => { setRegisterModalOpen(false); fetchData(); }}
        />
      </Modal>

      <Modal
        title={
          <Space>
            <EditOutlined style={{ color: brand.primary }} />
            <span>시험계획서 수정</span>
          </Space>
        }
        open={editId !== null}
        onCancel={handleEditClose}
        footer={null}
        width={1100}
        destroyOnClose
        styles={{ body: { height: 'calc(82vh - 55px)', padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
      >
        {editId !== null && (
          <ProtocolFormPage
            editId={String(editId)}
            onClose={handleEditClose}
          />
        )}
      </Modal>
    </>
  );
};

export default ProtocolListPage;
